import {Injectable} from '@angular/core';
import {EventsService} from '@webmate-web-core/shared/services/events/events.service';
import {catchError, filter, tap} from 'rxjs/operators';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {Observable, of, ReplaySubject, Subject} from 'rxjs';
import {TagApi} from '@webmate-web-core/shared/api/rest/tag.restapi';
import {CommentPayload, Tag, WebmateComment} from '@webmate-web-core/shared/types/tag';

/**
 * Provides access to Tags.
 */
@Injectable({
    providedIn: 'root'
})
export class TagService {

    private _tagsByResource: Map<string /* ResourceId */, Subject<Tag[]>> = new Map<string, ReplaySubject<Tag[]>>();

    private _commentsByResource: Map<string /* ResourceId */, Subject<WebmateComment[]>> = new Map<string, ReplaySubject<WebmateComment[]>>();

    constructor(private api: TagApi, private eventService: EventsService) {
        this._initEventBasedCacheInvalidator();
    }

    private _initEventBasedCacheInvalidator() {
        this.eventService.getAllEvents().pipe(filter(ev => ev.eventType.startsWith("Tag"))).subscribe(ev => {
            for (const key of this._commentsByResource.keys()) {
                this.getCommentsForResource$(key, true).subscribe();
            }
            // this._tagsByResource = Map<string, ReplaySubject<List<Tag>>>({})
        });
    }

    getTagsForResource$(resourceId: string, refresh: boolean = false): Observable<Tag[]> {
        if (!this._tagsByResource.has(resourceId)) {
            this._tagsByResource.set(resourceId, new ReplaySubject(1));
            refresh = true;
        }
        if (refresh) {
            this.api.getTagsForResource$(resourceId).subscribe(result => this._tagsByResource.get(resourceId).next(result));
        }
        return this._tagsByResource.get(resourceId).pipe(filter(c => !!c));
    }

    addTagsForResource$(projectId: string, resourceType: string, resourceId: string, tags: Tag[]): Observable<GenericApiResponse> {
        return this.api.addTagsToResource$(projectId, resourceType, resourceId, tags);
    }

    deleteTag$(resourceId: string, tagName: string): Observable<GenericApiResponse> {
        return this.api.deleteTag(resourceId, tagName);
    }


    getCommentsForResource$(resourceId: string, refresh: boolean = false): Observable<WebmateComment[]> {
        if (!this._commentsByResource.has(resourceId)) {
            this._commentsByResource.set(resourceId, new ReplaySubject(1))
            refresh = true;
        }

        if (refresh) {
            this.api.getCommentsForResource$(resourceId)
                .pipe(catchError(e => {
                    console.warn(`Could not retrieve comments for ${resourceId}`, e);
                    return of([]);
                }))
                .subscribe(result => this._commentsByResource.get(resourceId).next(result));
        }

        return this._commentsByResource.get(resourceId).pipe(filter(c => !!c));
    }

    addCommentToResource$(projectId: string, resourceType: string, resourceId: string, comment: CommentPayload): Observable<GenericApiResponse> {
        return this.api.addCommentToResource$(projectId, resourceType, resourceId, comment).pipe(tap(_ => this.getCommentsForResource$(resourceId, true)));
    };

}
